<template>
  <div
    class="tile-group"
    :class="`tile-group--${layout}`"
    role="radiogroup"
    :aria-disabled="disabled || undefined"
    :aria-label="ariaLabel || undefined"
    :data-locator="dataLocator"
  >
    <app-tooltip
      v-for="option in options"
      :key="`tile-option-${option.value}`"
      :disabled="!option.tooltip"
    >
      <template #activator="{ props }">
        <app-tile
          v-bind="props"
          selectable
          :layout="tileLayout"
          :selected="option.value === modelValue"
          :disabled="disabled || option.disabled"
          :label="option.label"
          :caption="option.caption"
          :icon="option.icon"
          :icon-color="option.iconColor"
          :icon-background-color="option.iconBackgroundColor"
          :data-locator="option.dataLocator || `${option.value}-tile`"
          @click="onSelect(option)"
        />
      </template>
      <span>{{ option.tooltip }}</span>
    </app-tooltip>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'

import AppTooltip from '@/common/AppTooltip.vue'

import AppTile from './AppTile.vue'

import type { TileGroupLayout, TileGroupType, TileLayout, TileOption } from './types'

/**
 * @displayName TileGroup
 * A group of selectable {@link AppTile}s behaving like a radio group.
 * Controlled through `v-model`; the model holds the `value` of the selected option.
 * Only `single-select` behavior is implemented; the `type` prop leaves room for a
 * future `multi-select` (checkbox) mode.
 */
export default defineComponent({
  name: 'AppTileGroup',
  components: { AppTile, AppTooltip },
  props: {
    /**
     * Value of the currently selected option (`v-model`)
     */
    modelValue: {
      type: String as PropType<string | null>,
      default: null,
    },
    /**
     * List of selectable options
     */
    options: {
      type: Array as PropType<TileOption[]>,
      required: true,
    },
    /**
     * Selection behavior of the group
     */
    type: {
      type: String as PropType<TileGroupType>,
      default: 'single-select',
    },
    /**
     * Layout applied to every tile in the group (forwarded as each tile's
     * `layout` prop)
     */
    tileLayout: {
      type: String as PropType<TileLayout>,
      default: 'horizontal',
    },
    /**
     * Layout of the group container:
     * - `horizontal-wrapped`: responsive grid that wraps onto several columns
     *   and fills the available width (default).
     * - `vertical`: single column, each tile taking the full available width.
     */
    layout: {
      type: String as PropType<TileGroupLayout>,
      default: 'horizontal-wrapped',
    },
    /**
     * Disables every tile in the group
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Accessible label for the radio group
     */
    ariaLabel: {
      type: String,
      default: '',
    },
    /**
     * `data-locator` of the group container
     */
    dataLocator: {
      type: String,
      default: 'tile-group',
    },
  },
  emits: {
    'update:modelValue': (value: string) => typeof value === 'string',
  },
  methods: {
    isDisabled (option: TileOption): boolean {
      return this.disabled || !!option.disabled
    },
    onSelect (option: TileOption) {
      if (this.isDisabled(option) || option.value === this.modelValue) {
        return
      }
      this.$emit('update:modelValue', option.value)
    },
  },
})
</script>

<style scoped lang="scss">
.tile-group {
  display: grid;
  gap: 16px;

  &--horizontal-wrapped {
    grid-template-columns: repeat(auto-fit, minmax(max(220px, calc((100% - 32px) / 3)), 1fr));
    grid-auto-rows: 1fr;
  }

  &--vertical {
    grid-template-columns: 1fr;
  }
}
</style>
